import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  searchUsers,
  approveUser,
  rejectUser,
  deactivateUser,
  reactivateUser,
  changeUserRole
} from '../../services/UserAdmin'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BlockIcon from '@mui/icons-material/Block'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import RestoreIcon from '@mui/icons-material/Restore'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Tooltip } from '@mui/material'
import './UserManagement.css'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/ja'

const PAGE_SIZE = 10

const UserManagement = () => {
  const { role } = useAuth()
  const queryClient = useQueryClient()

  const [inputUsername, setInputUsername] = useState('')

  const [searchParams, setSearchParams] = useState({
    username: '',
    active: '',
    dateFrom: '',
    dateTo: ''
  })

  const [filterActive, setFilterActive] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState(null)
  const [filterDateTo, setFilterDateTo] = useState(null)

  const [page, setPage] = useState(1)
  const [confirmAction, setConfirmAction] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', searchParams, page],
    queryFn: () =>
      searchUsers({
        username: searchParams.username,
        active: searchParams.active,
        createdFrom: searchParams.dateFrom || undefined,
        createdTo: searchParams.dateTo || undefined,
        page,
        size: PAGE_SIZE
      }),
    keepPreviousData: true
  })

  const users = data?.data || []
  const totalPages = data?.total_pages || 1
  const total = data?.total || 0

  const showSuccess = (msg) => {
    setSuccessMsg(msg)
    setTimeout(() => setSuccessMsg(null), 3000)
  }

  const actionMutation = useMutation({
    mutationFn: ({ actionFn, userId }) => actionFn(userId),
    onSuccess: (_, { actionLabel }) => {
      showSuccess(`${actionLabel}が完了しました`)
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setConfirmAction(null)
    }
  })

  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    setSearchParams({
      username: inputUsername,
      active: filterActive,
      dateFrom: filterDateFrom?.format('YYYY-MM-DD') ?? '',
      dateTo: filterDateTo?.format('YYYY-MM-DD') ?? ''
    })
  }

  const handleFilterActive = (val) => {
    setFilterActive(val)
    setPage(1)
    setSearchParams((prev) => ({ ...prev, active: val }))
  }

  const handleFilterDateFrom = (val) => {
    setFilterDateFrom(val)
    setPage(1)
    setSearchParams((prev) => ({
      ...prev,
      dateFrom: val?.format('YYYY-MM-DD') ?? ''
    }))
  }

  const handleFilterDateTo = (val) => {
    setFilterDateTo(val)
    setPage(1)
    setSearchParams((prev) => ({
      ...prev,
      dateTo: val?.format('YYYY-MM-DD') ?? ''
    }))
  }

  const requestConfirm = (actionFn, userId, actionLabel, userName) => {
    setConfirmAction({ actionFn, userId, actionLabel, userName })
  }

  if (role !== 'admin') {
    return (
      <div className='um-container'>
        <div className='um-error'>管理者権限が必要です</div>
      </div>
    )
  }

  const canApprove = (u) => !u.active && !u.approved_at && !u.deleted_at
  const canReject = (u) => !u.active && !u.approved_at && !u.deleted_at
  const canDeactivate = (u) => u.active && u.approved_at !== null
  const canReactivate = (u) => !u.active && u.approved_at !== null
  const canPromote = (u) => u.active && u.role !== 'admin'
  const canDemote = (u) => u.active && u.role === 'admin'

  return (
    <div className='um-container'>
      <h2 className='um-title'>ユーザー管理</h2>

      {successMsg && <div className='um-success'>{successMsg}</div>}
      {error && (
        <div className='um-error'>
          ユーザー情報の取得に失敗しました。再度お試しください。
        </div>
      )}
      {actionMutation.error && (
        <div className='um-error'>操作に失敗しました。再度お試しください。</div>
      )}

      <form className='um-search-row' onSubmit={handleSearch}>
        <div className='um-search-input-wrap'>
          <SearchIcon className='um-search-icon' />
          <input
            type='text'
            className='um-search-input'
            placeholder='ユーザー名で検索...'
            value={inputUsername}
            onChange={(e) => setInputUsername(e.target.value)}
          />
        </div>
        <button type='submit' className='um-btn um-btn-search'>
          検索
        </button>
      </form>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
        <div className='um-filters'>
          <div className='um-filter-group'>
            <label className='um-filter-label'>ステータス</label>
            <select
              className='um-select'
              value={filterActive}
              onChange={(e) => handleFilterActive(e.target.value)}
            >
              <option value=''>すべて</option>
              <option value='true'>有効</option>
              <option value='false'>無効</option>
            </select>
          </div>
          <div className='um-filter-group'>
            <label className='um-filter-label'>作成日 (から)</label>
            <DatePicker
              value={filterDateFrom}
              onChange={handleFilterDateFrom}
              format='YYYY年M月D日'
              slotProps={{ field: { clearable: true } }}
            />
          </div>
          <div className='um-filter-group'>
            <label className='um-filter-label'>作成日 (まで)</label>
            <DatePicker
              value={filterDateTo}
              onChange={handleFilterDateTo}
              format='YYYY年M月D日'
              slotProps={{ field: { clearable: true } }}
            />
          </div>
        </div>
      </LocalizationProvider>

      {/* Table */}
      <div className='um-table-wrap'>
        <table className='um-table'>
          <thead>
            <tr>
              <th className='um-th um-th-idx'>#</th>
              <th className='um-th'>ユーザー名</th>
              <th className='um-th'>メール</th>
              <th className='um-th um-th-status'>ステータス</th>
              <th className='um-th um-th-actions'>操作</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className='um-td um-loading'>
                  読み込み中...
                </td>
              </tr>
            )}
            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={5} className='um-td um-empty'>
                  ユーザーが見つかりません
                </td>
              </tr>
            )}
            {!isLoading &&
              users.map((user, idx) => (
                <tr key={user.id} className='um-tr'>
                  <td className='um-td um-td-idx'>
                    {(page - 1) * PAGE_SIZE + idx + 1}
                  </td>
                  <td className='um-td'>{user.username || '—'}</td>
                  <td className='um-td'>{user.email}</td>
                  <td className='um-td um-td-status'>
                    <span
                      className={`um-badge ${user.active ? 'um-badge-active' : 'um-badge-inactive'}`}
                    >
                      {user.active ? '有効' : '無効'}
                    </span>
                  </td>
                  <td className='um-td um-td-actions'>
                    {canApprove(user) && (
                      <Tooltip title='承認'>
                        <button
                          className='um-action-btn um-action-approve'
                          onClick={() =>
                            requestConfirm(
                              approveUser,
                              user.id,
                              '承認',
                              user.username || user.email
                            )
                          }
                        >
                          <CheckCircleIcon fontSize='small' />
                        </button>
                      </Tooltip>
                    )}
                    {canReject(user) && (
                      <Tooltip title='拒否'>
                        <button
                          className='um-action-btn um-action-reject'
                          onClick={() =>
                            requestConfirm(
                              rejectUser,
                              user.id,
                              '拒否',
                              user.username || user.email
                            )
                          }
                        >
                          <BlockIcon fontSize='small' />
                        </button>
                      </Tooltip>
                    )}
                    {canDeactivate(user) && (
                      <Tooltip title='無効化'>
                        <button
                          className='um-action-btn um-action-deactivate'
                          onClick={() =>
                            requestConfirm(
                              deactivateUser,
                              user.id,
                              '無効化',
                              user.username || user.email
                            )
                          }
                        >
                          <PersonOffIcon fontSize='small' />
                        </button>
                      </Tooltip>
                    )}
                    {canReactivate(user) && (
                      <Tooltip title='再有効化'>
                        <button
                          className='um-action-btn um-action-reactivate'
                          onClick={() =>
                            requestConfirm(
                              reactivateUser,
                              user.id,
                              '再有効化',
                              user.username || user.email
                            )
                          }
                        >
                          <RestoreIcon fontSize='small' />
                        </button>
                      </Tooltip>
                    )}
                    {canPromote(user) && (
                      <Tooltip title='管理者に昇格'>
                        <button
                          className='um-action-btn um-action-promote'
                          onClick={() =>
                            requestConfirm(
                              (id) => changeUserRole(id, 'admin'),
                              user.id,
                              '管理者に昇格',
                              user.username || user.email
                            )
                          }
                        >
                          <ArrowUpwardIcon fontSize='small' />
                        </button>
                      </Tooltip>
                    )}
                    {canDemote(user) && (
                      <Tooltip title='一般ユーザーに降格'>
                        <button
                          className='um-action-btn um-action-demote'
                          onClick={() =>
                            requestConfirm(
                              (id) => changeUserRole(id, 'user'),
                              user.id,
                              '一般ユーザーに降格',
                              user.username || user.email
                            )
                          }
                        >
                          <ArrowDownwardIcon fontSize='small' />
                        </button>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className='um-pagination'>
        <span className='um-pagination-info'>
          全 {total} 件中 {(page - 1) * PAGE_SIZE + 1} -
          {Math.min(page * PAGE_SIZE, total)} 件表示
        </span>
        <div className='um-pagination-controls'>
          <button
            className='um-page-btn'
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeftIcon fontSize='small' />
          </button>
          <span className='um-page-num'>
            {page} / {totalPages}
          </span>
          <button
            className='um-page-btn'
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRightIcon fontSize='small' />
          </button>
        </div>
      </div>

      {/* Confirm dialog */}
      {confirmAction && (
        <div className='um-overlay' onClick={() => setConfirmAction(null)}>
          <div className='um-dialog' onClick={(e) => e.stopPropagation()}>
            <p className='um-dialog-text'>
              <strong>{confirmAction.userName}</strong> を
              {confirmAction.actionLabel}しますか？
            </p>
            <div className='um-dialog-buttons'>
              <button
                className='um-btn um-btn-cancel'
                onClick={() => setConfirmAction(null)}
              >
                キャンセル
              </button>
              <button
                className='um-btn um-btn-confirm'
                disabled={actionMutation.isPending}
                onClick={() =>
                  actionMutation.mutate({
                    actionFn: confirmAction.actionFn,
                    userId: confirmAction.userId,
                    actionLabel: confirmAction.actionLabel
                  })
                }
              >
                {actionMutation.isPending ? '処理中...' : '確認'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
